import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { format, subHours, subDays } from 'date-fns';

const API_URL = 'https://koryzna-capital-backend-948732212792.europe-central2.run.app';

// --- MOTYW: TOTAL BLACK TERMINAL ---
const theme = {
  bgMain: '#000000',
  bgCard: '#000000',
  border: '#666666',
  textPrimary: '#c0c0c0',
  textSecondary: '#808080',
  accentBlue: '#64b5f6',
  accentGreen: '#81c784',
  accentRed: '#e57373',
  gridLine: '#222222'
};

const s = {
  container: { 
    padding: '30px 20px', 
    maxWidth: '1800px', 
    margin: '0 auto', 
    color: theme.textPrimary,
    minHeight: '100vh',
    backgroundColor: theme.bgMain 
  },
  mainLayout: { display: 'flex', gap: '30px', flexWrap: 'wrap' },
  paintBox: { 
    border: `2px solid ${theme.border}`,
    backgroundColor: theme.bgCard,
    padding: '25px', 
    marginBottom: '25px'
  },
  header: { 
    fontSize: '1.5rem', 
    fontWeight: '700', 
    marginBottom: '20px', 
    color: theme.textPrimary,
    textTransform: 'uppercase',
    letterSpacing: '1px'
  },
  tableGrid: { 
    display: 'grid', 
    gridTemplateColumns: '110px repeat(6, 1fr)', 
    borderTop: `2px solid ${theme.border}`, 
    borderLeft: `2px solid ${theme.border}`,
    backgroundColor: theme.bgCard,
    marginTop: '20px'
  },
  cell: { 
    borderRight: `2px solid ${theme.border}`, 
    borderBottom: `2px solid ${theme.border}`, 
    padding: '12px 8px', 
    textAlign: 'center', 
    fontWeight: '500', 
    fontSize: '0.9rem', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center',
    color: theme.textSecondary
  },
  cellHeader: { 
    backgroundColor: '#1a1a1a', 
    color: theme.textPrimary,
    fontWeight: '700',
    textTransform: 'uppercase',
    fontSize: '0.85rem',
    borderRight: `2px solid ${theme.border}`,
    borderBottom: `2px solid ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px'
  },
  green: { color: theme.accentGreen, fontWeight: '700' },
  red: { color: theme.accentRed, fontWeight: '700' },
  chartTabs: { display: 'flex', gap: '-2px', marginBottom: '15px', marginLeft: 'auto' },
  tab: (isActive) => ({
    border: `2px solid ${theme.border}`,
    padding: '6px 14px',
    fontWeight: '700',
    fontSize: '0.8rem',
    cursor: 'pointer',
    backgroundColor: isActive ? theme.border : theme.bgCard, 
    color: isActive ? '#000000' : theme.textSecondary,
    transition: 'none',
    marginLeft: '-2px'
  })
};

// --- CUSTOM TOOLTIP (NAPRAWIONY FORMAT DATY) ---
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const profitPln = data.profitPln || 0;
    const profitUsd = data.profitUsd || 0;
    const capitalPln = data.pln || 0;

    // Formatujemy datę wewnątrz tooltipa (label to teraz pełny ISO string)
    let displayDate = label;
    try {
        displayDate = format(new Date(label), 'dd.MM HH:mm');
    } catch (e) { /* fallback */ }

    return (
      <div style={{ border: `2px solid ${theme.border}`, backgroundColor: theme.bgCard, padding: '12px', minWidth: '180px' }}>
        <p style={{ fontWeight: '700', marginBottom: '8px', color: theme.textPrimary, borderBottom: `2px solid ${theme.border}`, paddingBottom: 5 }}>
          {displayDate}
        </p>
        <p style={{ margin: '5px 0', color: profitPln >= 0 ? theme.accentGreen : theme.accentRed, fontWeight: '700', fontSize: '1.1rem' }}>
          Zysk (PLN): {profitPln > 0 ? '+' : ''}{profitPln.toFixed(2)} PLN
        </p>
        <p style={{ margin: '5px 0', color: theme.accentBlue, fontSize: '1rem' }}>
          Zysk (USD): {profitUsd > 0 ? '+' : ''}{profitUsd.toFixed(2)} $
        </p>
        <p style={{ margin: '10px 0 0 0', fontSize: '0.85rem', color: theme.textSecondary, borderTop: `1px solid ${theme.border}`, paddingTop: 5 }}>
          Kapitał: {capitalPln.toFixed(2)} PLN
        </p>
      </div>
    );
  }
  return null;
};

// --- SEKCJA PORTFELA ---
const WalletSection = ({ title, data, chartData, depositsInfo, withdrawalsInfo, isSimulation = false }) => {
  const [timeRange, setTimeRange] = useState('30D');

  const renderPnl = (val, isPercent = false, suffix = '') => {
    if (val === undefined || val === null) return "-";
    const num = parseFloat(val);
    if (isNaN(num)) return "-";
    const formatted = num.toFixed(2);
    const text = isPercent ? `${formatted}%` : `${formatted} ${suffix}`;
    if (Math.abs(num) < 0.01) return <span style={{color: theme.textSecondary}}>{text}</span>;
    return <span style={num > 0 ? s.green : s.red}>{num > 0 ? '+' : ''}{text}</span>;
  };

  const current = data.current || { usd: 0, pln: 0 };
  const pnl = data.pnl || {};

  const getFilteredData = () => {
    if (!chartData || chartData.length === 0) return [];
    const now = new Date();
    let cutoffDate = new Date();
    switch (timeRange) {
      case '1H': cutoffDate = subHours(now, 1); break;
      case '1D': cutoffDate = subDays(now, 1); break;
      case '7D': cutoffDate = subDays(now, 7); break;
      case '30D': cutoffDate = subDays(now, 30); break;
      case '90D': cutoffDate = subDays(now, 90); break;
      case '180D': cutoffDate = subDays(now, 180); break;
      default: cutoffDate = subDays(now, 30);
    }
    return chartData.filter(item => new Date(item.date) >= cutoffDate);
  };

  const filteredHistory = getFilteredData();
  
  // Obliczanie bazowe (punkt 0 na wykresie)
  const baselinePln = filteredHistory.length > 0 ? filteredHistory[0].pln : 0;
  const baselineUsd = filteredHistory.length > 0 ? filteredHistory[0].usd : 0;

  const processedChartData = filteredHistory.map(item => ({
      ...item,
      profitPln: item.pln - baselinePln,
      profitUsd: item.usd - baselineUsd
  }));

  const renderTabs = () => (
    <div style={s.chartTabs}>
      {['1H','1D','7D','30D','90D','180D'].map(t => (
        <div key={t} style={s.tab(timeRange === t)} onClick={() => setTimeRange(t)}>
          {t}
        </div>
      ))}
    </div>
  );

  // Funkcja formatująca datę na osi X (żeby nie było duplikatów w dataKey)
  const formatXAxis = (tickItem) => {
      try {
        return format(new Date(tickItem), 'dd.MM');
      } catch (e) {
        return tickItem;
      }
  };

  return (
    <div style={{marginBottom: '50px'}}>
      <div style={s.mainLayout}>
        {/* LEWA KOLUMNA: TABELE */}
        <div style={{ flex: '0 0 680px' }}>
          <div style={s.paintBox}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20}}>
                <div style={s.header}>{title}</div>
                <div style={{fontSize: '0.8rem', color: theme.textSecondary, textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 700}}>STATYSTYKI</div>
            </div>
            
            <div style={{display: 'flex', borderBottom: `2px solid ${theme.border}`, paddingBottom: 25}}>
                <div style={{flex: 1, borderRight: `2px solid ${theme.border}`, paddingRight: 20}}>
                    <div style={{fontSize: '0.9rem', color: theme.textSecondary, marginBottom: 8, textTransform: 'uppercase'}}>Kapitał (USD)</div>
                    <div style={{fontSize: '2.6rem', fontWeight: '900', color: theme.textPrimary, letterSpacing: '-1px'}}>{current.usd?.toFixed(2)} $</div>
                </div>
                <div style={{flex: 1, paddingLeft: 20}}>
                    <div style={{fontSize: '0.9rem', color: theme.textSecondary, marginBottom: 8, textTransform: 'uppercase'}}>Kapitał (PLN)</div>
                    <div style={{fontSize: '2.6rem', fontWeight: '900', color: theme.textPrimary, letterSpacing: '-1px'}}>{current.pln?.toFixed(2)} zł</div>
                </div>
            </div>

            <div style={s.tableGrid}>
                <div style={s.cellHeader}>Okres</div>
                {['1H','1D','7D','30D','90D','180D'].map(h => <div key={h} style={s.cellHeader}>{h}</div>)}
                
                <div style={{...s.cell, color: theme.textPrimary, fontWeight: 700, backgroundColor: '#1a1a1a'}}>PnL (%)</div>
                <div style={s.cell}>{renderPnl(pnl['1H']?.pnlPercent, true)}</div>
                <div style={s.cell}>{renderPnl(pnl['1D']?.pnlPercent, true)}</div>
                <div style={s.cell}>{renderPnl(pnl['7D']?.pnlPercent, true)}</div>
                <div style={s.cell}>{renderPnl(pnl['30D']?.pnlPercent, true)}</div>
                <div style={s.cell}>{renderPnl(pnl['90D']?.pnlPercent, true)}</div>
                <div style={s.cell}>{renderPnl(pnl['180D']?.pnlPercent, true)}</div>

                <div style={{...s.cell, color: theme.textPrimary, fontWeight: 700, backgroundColor: '#1a1a1a'}}>PnL ($)</div>
                <div style={s.cell}>{renderPnl(pnl['1H']?.pnlUSD)}</div>
                <div style={s.cell}>{renderPnl(pnl['1D']?.pnlUSD)}</div>
                <div style={s.cell}>{renderPnl(pnl['7D']?.pnlUSD)}</div>
                <div style={s.cell}>{renderPnl(pnl['30D']?.pnlUSD)}</div>
                <div style={s.cell}>{renderPnl(pnl['90D']?.pnlUSD)}</div>
                <div style={s.cell}>{renderPnl(pnl['180D']?.pnlUSD)}</div>

                <div style={{...s.cell, color: theme.textPrimary, fontWeight: 700, backgroundColor: '#1a1a1a'}}>PnL (PLN)</div>
                 <div style={s.cell}>{renderPnl(pnl['1H']?.pnlPLN)}</div>
                 <div style={s.cell}>{renderPnl(pnl['1D']?.pnlPLN)}</div>
                 <div style={s.cell}>{renderPnl(pnl['7D']?.pnlPLN)}</div>
                 <div style={s.cell}>{renderPnl(pnl['30D']?.pnlPLN)}</div>
                 <div style={s.cell}>{renderPnl(pnl['90D']?.pnlPLN)}</div>
                 <div style={s.cell}>{renderPnl(pnl['180D']?.pnlPLN)}</div>
            </div>
            {isSimulation && <div style={{marginTop: 10, fontSize: '0.8rem', color: theme.textSecondary, fontStyle: 'italic'}}>* Obliczone na podstawie udziału {data.sharePercent?.toFixed(2)}% w koncie głównym</div>}
          </div>

          <div style={{display: 'flex', gap: '25px'}}>
             <div style={{...s.paintBox, flex: 1, minHeight: '140px'}}>
                <div style={{textAlign: 'center', fontWeight: '700', marginBottom: 15, borderBottom: `2px solid ${theme.border}`, paddingBottom: 8, color: theme.textPrimary, textTransform: 'uppercase'}}>Wpłaty</div>
                <div style={{fontSize: '0.95rem', whiteSpace: 'pre-line', color: theme.textSecondary, lineHeight: 1.5}}>{depositsInfo}</div>
             </div>
             <div style={{...s.paintBox, flex: 1, minHeight: '140px'}}>
                <div style={{textAlign: 'center', fontWeight: '700', marginBottom: 15, borderBottom: `2px solid ${theme.border}`, paddingBottom: 8, color: theme.textPrimary, textTransform: 'uppercase'}}>Wypłaty</div>
                <div style={{fontSize: '0.95rem', color: theme.textSecondary, fontStyle: 'italic', textAlign: 'center'}}>{withdrawalsInfo}</div>
             </div>
          </div>
        </div>

        {/* PRAWA KOLUMNA: WYKRESY */}
        <div style={{ flex: 1, minWidth: '550px' }}>
            
            {/* Wykres 1 - KAPITAŁ */}
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', paddingRight: 0, marginBottom: 10}}>
                <div style={{fontSize: '1.1rem', fontWeight: '700', color: theme.textPrimary, textTransform: 'uppercase'}}>KAPITAŁ (USD)</div>
                {renderTabs()}
            </div>
            <div style={{...s.paintBox, height: '340px', padding: '15px 25px 15px 5px'}}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={processedChartData}>
                        <CartesianGrid strokeDasharray="0" stroke={theme.gridLine} vertical={false} />
                        {/* ZMIANA: dataKey="date" + tickFormatter */}
                        <XAxis 
                            dataKey="date" 
                            tickFormatter={formatXAxis}
                            style={{fontSize: '0.8rem', fontWeight: 700}} 
                            tick={{fill: theme.textSecondary}} 
                            axisLine={{stroke: theme.border, strokeWidth: 2}} 
                            tickLine={false} 
                            dy={10} 
                            minTickGap={30}
                        />
                        <YAxis domain={['auto', 'auto']} style={{fontSize: '0.8rem', fontWeight: 700}} tick={{fill: theme.textSecondary}} axisLine={{stroke: theme.border, strokeWidth: 2}} tickLine={false} dx={-10} />
                        <Tooltip contentStyle={{backgroundColor: theme.bgCard, border: `2px solid ${theme.border}`, color: theme.textPrimary}} itemStyle={{color: theme.textPrimary}} formatter={(value) => [`${value.toFixed(2)} $`, 'Kapitał']} labelFormatter={(label) => format(new Date(label), 'dd.MM HH:mm')} />
                        <Line type="monotone" dataKey="usd" stroke={theme.textPrimary} strokeWidth={3} dot={false} activeDot={{r: 6, fill: theme.textPrimary, stroke: theme.bgCard, strokeWidth: 2}} />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            {/* Wykres 2 - ZYSK */}
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', paddingRight: 0, marginTop: 35, marginBottom: 10}}>
                <div style={{fontSize: '1.1rem', fontWeight: '700', color: theme.textPrimary, textTransform: 'uppercase'}}>ZYSK (PLN)</div>
                <div style={{width: 50}}></div> 
            </div>
            <div style={{...s.paintBox, height: '340px', padding: '15px 25px 15px 5px'}}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={processedChartData}>
                        <CartesianGrid strokeDasharray="0" stroke={theme.gridLine} vertical={false} />
                        {/* ZMIANA: dataKey="date" + tickFormatter */}
                        <XAxis 
                            dataKey="date" 
                            tickFormatter={formatXAxis}
                            style={{fontSize: '0.8rem', fontWeight: 700}} 
                            tick={{fill: theme.textSecondary}} 
                            axisLine={{stroke: theme.border, strokeWidth: 2}} 
                            tickLine={false} 
                            dy={10} 
                            minTickGap={30}
                        />
                        <YAxis domain={['auto', 'auto']} style={{fontSize: '0.8rem', fontWeight: 700}} tick={{fill: theme.textSecondary}} axisLine={{stroke: theme.border, strokeWidth: 2}} tickLine={false} dx={-10} />
                        <ReferenceLine y={0} stroke={theme.border} strokeWidth={2} />
                        
                        <Tooltip content={<CustomTooltip />} />
                        
                        <Line type="monotone" dataKey="profitPln" stroke={theme.accentBlue} strokeWidth={3} dot={false} activeDot={{r: 6, fill: theme.accentBlue, stroke: theme.bgCard, strokeWidth: 2}} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
      </div>
    </div>
  );
};

// --- GŁÓWNA APLIKACJA ---
function App() {
  const [data, setData] = useState(null);
  const [totalHistory, setTotalHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rate, setRate] = useState(4.0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/api/dashboard`);
        setData(res.data);
        if (res.data.meta && res.data.meta.rate) setRate(res.data.meta.rate);

        const chartRes = await axios.get(`${API_URL}/api/chart-history?days=180`);
        const formattedChart = chartRes.data.map(item => ({
          ...item,
          // 'dateShort' zachowujemy opcjonalnie, ale wykresy teraz używają 'date'
          dateShort: format(new Date(item.date), 'dd.MM'),
        }));
        setTotalHistory(formattedChart);
        setLoading(false);
      } catch (err) {
        console.error("Błąd API:", err);
        setError("Błąd połączenia z backendem.");
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <div style={{padding: 40, backgroundColor: theme.bgMain, color: theme.textPrimary, minHeight: '100vh', fontFamily: 'monospace'}}>ŁADOWANIE DANYCH...</div>;
  if (error) return <div style={{padding: 40, color: theme.accentRed, backgroundColor: theme.bgMain, minHeight: '100vh', fontFamily: 'monospace'}}>{error}</div>;
  if (!data || !data.config) return <div style={{padding: 40, backgroundColor: theme.bgMain, color: theme.textPrimary, fontFamily: 'monospace'}}>BRAK KONFIGURACJI.</div>;

  const { config, total } = data;
  const REF_RATE = config.refRate; 
  const TOTAL_USD_REF = config.totalUsdRef;

  const calculateWalletData = (walletKey) => {
     const walletConfig = config.wallets[walletKey];
     if (!walletConfig) return null; 

     const depositPln = walletConfig.depositPln;
     const usdPart = depositPln / REF_RATE;
     const shareRatio = usdPart / TOTAL_USD_REF;
     const currentUsd = total.current.usd * shareRatio;
     const currentPln = currentUsd * rate;

     const history = totalHistory.map(point => ({
        ...point,
        usd: point.usd * shareRatio,
        pln: point.pln * shareRatio
     }));

     const scaledPnl = {};
     if (total.pnl) {
       Object.keys(total.pnl).forEach(periodKey => {
         const p = total.pnl[periodKey];
         scaledPnl[periodKey] = {
           pnlPercent: p.pnlPercent,
           pnlUSD: p.pnlUSD * shareRatio,
           pnlPLN: p.pnlPLN * shareRatio
         };
       });
     }

     return {
        title: `PORTFEL: ${walletConfig.name.toUpperCase()}`,
        data: {
            current: { usd: currentUsd, pln: currentPln },
            pnl: scaledPnl,
            sharePercent: shareRatio * 100
        },
        chartData: history,
        info: `START (09.12.2025):\n${depositPln.toLocaleString()} PLN | ${usdPart.toFixed(2)} USD`
     };
  };

  const parents = calculateWalletData('parents');
  const kamil = calculateWalletData('kamil');

  return (
    <div style={s.container}>
      <WalletSection 
        title="PORTFEL GŁÓWNY (HYPERLIQUID)"
        data={total}
        chartData={totalHistory}
        depositsInfo={`KAPITAŁ ŁĄCZNY\nUSD: ${(total.current.usd).toFixed(2)} $\nPLN: ${(total.current.pln).toFixed(2)} zł`}
        withdrawalsInfo="BRAK WYPŁAT"
      />
      {parents && <WalletSection title={parents.title} data={parents.data} chartData={parents.chartData} depositsInfo={parents.info} withdrawalsInfo="BRAK WYPŁAT" isSimulation={true} />}
      {kamil && <WalletSection title={kamil.title} data={kamil.data} chartData={kamil.chartData} depositsInfo={kamil.info} withdrawalsInfo="BRAK WYPŁAT" isSimulation={true} />}
    </div>
  );
}

export default App;